import styled from 'styled-components';
import {
  IS_TAB_OPEN_DATE,
  SATELLITE_END_DATE,
  SHOW_QR_DATE,
  useDateHook,
} from '../hook/useDateHook';
import { getEnv, isPc, parseQueryString } from '../utils';
import { useState, useMemo } from 'react';
import dayjs from 'dayjs';
import { infoStore } from '../store/infoStore';

export const TestBox = () => {
  const [visible, setVisible] = useState(false);
  const [selectedYear, setSelectedYear] = useState(2025);
  const [selectedMonth, setSelectedMonth] = useState(dayjs().month() + 1);
  const [selectedDay, setSelectedDay] = useState(1);
  const [selectedTime, setSelectedTime] = useState('00:00');
  const [selectedMinute, setSelectedMinute] = useState('00');
  const env = getEnv();
  const { setTodayDate, today, setToday } = useDateHook();
  const { info } = infoStore();

  // 년도 선택 옵션 (2025, 2026)
  const yearOptions = useMemo(
    () => [
      { value: 2025, label: '2025년' },
      { value: 2026, label: '2026년' },
    ],
    [],
  );

  // 월 선택 옵션 (1월 ~ 12월)
  const monthOptions = useMemo(
    () => Array.from({ length: 12 }, (_, i) => ({ value: i + 1, label: `${i + 1}월` })),
    [],
  );

  // 시간 선택 옵션 (00:00 ~ 23:00)
  const timeOption = useMemo(
    () =>
      Array.from({ length: 24 }, (_, i) => {
        return { value: `${i.toString().padStart(2, '0')}:00`, label: `${i}시` };
      }),
    [],
  );

  // 분 선택 옵션 (00 ~ 59)
  const minuteOption = useMemo(
    () =>
      Array.from({ length: 60 }, (_, i) => {
        const minute = i.toString().padStart(2, '0');
        return { value: minute, label: `${minute}분` };
      }),
    [],
  );

  // 일 선택 옵션 (1일 ~ 31일)
  const dayOptions = useMemo(() => {
    return Array.from({ length: 31 }, (_, i) => ({
      value: i + 1,
      label: `${i + 1}일`,
    }));
  }, []);

  // 사용자 정의 날짜 설정
  const applyCustomDate = () => {
    const hour = selectedTime.split(':')[0];
    const date = dayjs(
      `${selectedYear}-${selectedMonth.toString().padStart(2, '0')}-${selectedDay
        .toString()
        .padStart(2, '0')}T${hour}:${selectedMinute}:00`,
    );
    if (date.isValid()) {
      setToday(date.format('YYYY-MM-DDTHH:mm:ss'));
    }
  };

  if (isPc()) return null; // PC 환경에서는 표시하지 않음
  if (env === 'live') return null;

  return (
    <>
      {!visible && <ToggleButton onClick={() => setVisible(true)}>DEV</ToggleButton>}

      {visible && (
        <Wrap>
          <Header>
            <HeaderTitle>Dev Tools</HeaderTitle>
            <Close onClick={() => setVisible(false)}>×</Close>
          </Header>

          <Title>현재 날짜: {today.format('YYYY-MM-DD HH:mm:ss')}</Title>

          <InfoBox>
            <InfoItem>API에서 넘겨준 버전: {info.version ? info.version : '버전 없음'}</InfoItem>
            <InfoItem>클라에서 넘겨준 버전: v{parseQueryString().v}</InfoItem>
          </InfoBox>

          <ButtonGroup>
            <ButtonLabel>날짜 프리셋</ButtonLabel>
            <Button onClick={() => setTodayDate()}>오늘로 설정</Button>
            <Button onClick={() => setToday(dayjs(IS_TAB_OPEN_DATE).add(1, 'second'))}>
              {dayjs(IS_TAB_OPEN_DATE).add(1, 'second').format('MM/DD')} - 새틀라이트 시작
            </Button>
            <Button onClick={() => setToday(dayjs(SATELLITE_END_DATE).add(1, 'second'))}>
              {dayjs(SATELLITE_END_DATE).add(1, 'second').format('YYYY/MM/DD')} - 새틀라이트 종료
            </Button>
            <Button onClick={() => setToday(dayjs(SHOW_QR_DATE).add(1, 'second'))}>
              {dayjs(SHOW_QR_DATE).add(1, 'second').format('YYYY/MM/DD')} - QR 노출
            </Button>
          </ButtonGroup>

          <Divider />

          <ButtonGroup>
            <ButtonLabel>페이지 URL</ButtonLabel>
            <Button
              onClick={() => {
                navigator.clipboard.writeText(window.location.href);
                alert('URL이 클립보드에 복사되었습니다!');
              }}
            >
              현재 페이지 URL 복사
            </Button>
          </ButtonGroup>

          <Divider />

          <ButtonGroup>
            <ButtonLabel>날짜 직접 선택</ButtonLabel>
            <DateSelectContainer>
              <SelectBox
                value={selectedYear}
                onChange={(e) => setSelectedYear(Number(e.target.value))}
              >
                {yearOptions.map((option) => (
                  <option key={`year-${option.value}`} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </SelectBox>
              <SelectBox
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(Number(e.target.value))}
              >
                {monthOptions.map((option) => (
                  <option key={`month-${option.value}`} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </SelectBox>
              <SelectBox
                value={selectedDay}
                onChange={(e) => setSelectedDay(Number(e.target.value))}
              >
                {dayOptions.map((option) => (
                  <option key={`day-${option.value}`} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </SelectBox>
            </DateSelectContainer>
            <DateSelectContainer>
              <SelectBox value={selectedTime} onChange={(e) => setSelectedTime(e.target.value)}>
                {timeOption.map((option) => (
                  <option key={`time-${option.value}`} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </SelectBox>
              <SelectBox value={selectedMinute} onChange={(e) => setSelectedMinute(e.target.value)}>
                {minuteOption.map((option) => (
                  <option key={`minute-${option.value}`} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </SelectBox>
            </DateSelectContainer>
            <ApplyButton onClick={applyCustomDate}>적용</ApplyButton>
          </ButtonGroup>
        </Wrap>
      )}
    </>
  );
};

const Wrap = styled.div`
  position: fixed;
  top: 10px;
  left: 10px;
  z-index: 999;
  width: 280px;
  max-height: 85vh;
  overflow-y: auto;
  border: 1px solid #e0e0e0;
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  background-color: #fafafa;
  padding: 16px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08);

  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: #ccc;
    border-radius: 2px;

    &:hover {
      background: #aaa;
    }
  }
`;

const Title = styled.p`
  font-size: 13px;
  color: #222;
  margin-bottom: 12px;
  font-weight: 600;
  background-color: #fff;
  padding: 10px 12px;
  border-radius: 8px;
  border: 1px solid #eee;
`;

const InfoBox = styled.div`
  background-color: #fff;
  border: 1px solid #eee;
  border-radius: 8px;
  padding: 12px;
  margin-bottom: 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const InfoItem = styled.div`
  font-size: 12px;
  color: #333;
  display: flex;
  align-items: center;
  padding: 6px 0;
  border-bottom: 1px solid #f5f5f5;

  &:last-child {
    border-bottom: none;
    padding-bottom: 0;
  }

  &:first-child {
    padding-top: 0;
  }
`;

const Button = styled.button`
  margin-bottom: 6px;
  font-size: 12px;
  padding: 10px 12px;
  background-color: #fff;
  border: 1px solid #e5e5e5;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.15s ease;
  font-weight: 500;
  color: #222;
  text-align: left;

  &:hover {
    background-color: #f5f5f5;
    border-color: #d0d0d0;
  }

  &:active {
    background-color: #eee;
  }
`;

const Close = styled.button`
  position: absolute;
  top: 8px;
  right: 8px;
  width: 22px;
  height: 22px;
  border: 1px solid #ddd;
  background-color: #fff;
  color: #555;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.15s;

  &:hover {
    background-color: #f5f5f5;
    color: #333;
    border-color: #ccc;
  }
`;

const Divider = styled.hr`
  margin: 12px 0;
  border: 0;
  height: 1px;
  background-color: #eee;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 14px;
  padding-bottom: 12px;
  border-bottom: 1px solid #eee;
`;

const HeaderTitle = styled.h3`
  font-size: 14px;
  color: #222;
  margin: 0;
  font-weight: 600;
`;

const ButtonGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const ButtonLabel = styled.div`
  font-size: 11px;
  font-weight: 600;
  margin-bottom: 8px;
  margin-top: 4px;
  color: #555;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const SelectBox = styled.select`
  flex: 1;
  padding: 10px 8px;
  font-size: 12px;
  border: 1px solid #e5e5e5;
  border-radius: 8px;
  background-color: #fff;
  color: #222;
  font-weight: 500;
  cursor: pointer;
  min-width: 0;

  &:focus {
    outline: none;
    border-color: #ccc;
  }
`;

const DateSelectContainer = styled.div`
  display: flex;
  gap: 6px;
  margin-bottom: 8px;
`;

const ApplyButton = styled.button`
  width: 100%;
  padding: 10px;
  font-size: 12px;
  background-color: #333;
  color: #fff;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.15s;
  font-weight: 500;

  &:hover {
    background-color: #555;
  }

  &:active {
    background-color: #222;
  }
`;

const ToggleButton = styled.button`
  position: fixed;
  top: 10px;
  left: 10px;
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background-color: #fff;
  color: #333;
  font-size: 9px;
  font-weight: 700;
  letter-spacing: -0.5px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #e0e0e0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  cursor: pointer;
  z-index: 999;
  transition: all 0.15s;

  &:hover {
    background-color: #f5f5f5;
    border-color: #ccc;
  }
`;
